import React from 'react'
import PropTypes from 'prop-types'
import { DialogContent, DialogTitle, Divider, IconButton, Dialog as MuiDialog, Typography, Box } from '@mui/material'
import close from 'images/close.svg'
import Badge from './Badge'
import { usePriceComparisonChanges } from 'hooks/usePriceComparisonChanges'
import PriceChangesInfo from './PriceChangesInfo'
import { numberFormat, numberToCurrencyFormat } from 'utils/number-format-util'
import { useTranslation } from 'react-i18next'

const OldPriceComparisonDialog = ({ priceData, isDialogOpen = false, closeDialog = () => {} }) => {
    const { t } = useTranslation()
    const { increasesResponse = {}, decreaseResponse = {} } = priceData

    const {
        priceIncrease,
        priceDecrease,
        totalChanges,
        formattedIncreaseTotal,
        formattedDecreaseTotal,
        formattedTotalPrice,
        increasesPopOverData,
        decreasesPopOverData,
        totalPopOverData,
        transformOrigin
    } = usePriceComparisonChanges(priceData)

    return (
        <MuiDialog
            fullScreen
            open={isDialogOpen}
            onClose={closeDialog}
            disableEscapeKeyDown={true}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            data-testid="disclosures-dialog"
            sx={{
                color: 'common.fordBlue.main',
                zIndex: 3000,
                textAlign: 'left',
                padding: '0px',
                '.MuiModal-backdrop': {
                    backgroundColor: 'action.disabledBackground'
                },
                '.MuiDialog-paper': {
                    color: 'common.fordBlue.main',
                    backgroundColor: 'background.default',
                    borderRadius: { xs: 0, sm: 4 },
                    maxWidth: { xs: 390, md: 570, lg: 871 },
                    padding: '0px 0px 15px',
                    height: 'auto',
                    maxHeight: { xs: '100%', sm: '98%' }
                }
            }}
        >
            <DialogTitle id="alert-dialog-title">
                <Typography
                    variant="regularTitle"
                    tabIndex={0}
                    sx={{
                        lineHeight: { xs: '48px', md: '48px' },
                        typography: { xs: 'regularTitle', md: 'smallHeadline2' },
                        color: 'common.neutralBlack.dark'
                    }}
                >
                    {t('price_changes')}
                </Typography>
                <IconButton
                    id="close"
                    aria-label="close"
                    onClick={closeDialog}
                    sx={{
                        color: 'common.fordBlue.main',
                        p: 2,
                        position: 'absolute',
                        right: '4px',
                        top: '4px'
                    }}
                >
                    <img src={close} alt={'close modal'} width={24} height={24} />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                    padding: '0px'
                }}
            >
                <Typography
                    variant="regularBody1"
                    color="common.gray.6"
                    tabIndex={0}
                    sx={{
                        padding: '0 30px 24px'
                    }}
                    data-testid="total-changes"
                >
                    {totalChanges > 1
                        ? t('old_price_changes_description', { total: totalChanges })
                        : t('old_price_change_description', { total: totalChanges })}
                </Typography>

                <Typography
                    variant="boldBody1"
                    sx={{
                        color: 'common.black.main',
                        alignItems: 'center',
                        display: 'flex',
                        overflow: 'initial',
                        justifyContent: 'center',
                        padding: '0px 24px'
                    }}
                >
                    <Typography tabIndex={0} component="span" variant="boldBody1">
                        {t('total_change')}
                    </Typography>
                    <PriceChangesInfo
                        popOverData={totalPopOverData}
                        transformOrigin={transformOrigin}
                        isTotalPopOver={true}
                    />
                </Typography>

                <Box
                    tabIndex={0}
                    sx={{
                        display: 'flex',
                        gap: '3px',
                        alignItems: 'baseline',
                        component: 'span',
                        justifyContent: 'center',
                        padding: '10px',
                        margin: '0 55px 0 0'
                    }}
                    data-testid="total-amt"
                >
                    <Typography
                        variant="smallDisplay1"
                        display="flex"
                        component="span"
                        mb="auto"
                        sx={{
                            color: 'common.neutralBlack.main',
                            display: 'flex',
                            position: 'relative',
                            bottom: '10px'
                        }}
                    >
                        {formattedTotalPrice ? (Math.sign(formattedTotalPrice) === -1 && '-') || '+' : ''}
                    </Typography>
                    <Typography
                        variant="lightBody2"
                        component="span"
                        sx={{
                            color: 'common.neutralBlack.main',
                            position: 'relative',
                            bottom: '15px',
                            fontWeight: 'bold'
                        }}
                    >
                        $
                    </Typography>
                    <Typography
                        variant="smallHeadline2"
                        component="span"
                        sx={{ fontWeight: 'bold', color: 'common.neutralBlack.main' }}
                    >
                        {numberFormat(formattedTotalPrice?.replace('-', '').replace('+', ''))}
                    </Typography>
                </Box>

                <Divider
                    sx={{ height: '8px', margin: '0 0 24px', backgroundColor: 'common.gray.7' }}
                    orientation="vertical"
                    aria-hidden="true"
                ></Divider>

                {Object.keys(increasesResponse)?.length > 0 && (
                    <>
                        <Box
                            sx={{
                                margin: { xs: '0 30px', md: '0 70px' }
                            }}
                        >
                            <Typography
                                variant="boldBody1"
                                sx={{
                                    py: 2,
                                    color: 'common.neutralBlack.dark'
                                }}
                                data-testid="increases"
                                data-totalinc={Object.keys(increasesResponse)?.length}
                            >
                                <Typography tabIndex={0} component="span" variant="boldBody1">
                                    {t('increases')}
                                </Typography>
                                <PriceChangesInfo
                                    popOverData={increasesPopOverData}
                                    transformOrigin={transformOrigin}
                                    isTotalPopOver={false}
                                />
                            </Typography>

                            <Divider
                                sx={{ height: '1px', backgroundColor: 'common.black.main' }}
                                orientation="vertical"
                                aria-hidden="true"
                            ></Divider>
                        </Box>

                        {priceIncrease?.details?.map((data) => (
                            <Box
                                key={data.name}
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    gap: { xs: 2, lg: 16 },
                                    py: 2,
                                    margin: { xs: '0 30px', md: '0 70px' }
                                }}
                            >
                                <Typography
                                    tabIndex={0}
                                    variant="regularBody2"
                                    display="block"
                                    sx={{
                                        color: 'common.gray.6',
                                        py: 2,
                                        textAlign: 'left',
                                        textWrap: 'balance',
                                        flex: { xs: 1.5, md: 2.1 }
                                    }}
                                    data-testid="increase-label"
                                >
                                    {data?.name}
                                    {data?.badge && data.badgeContent && (
                                        <Badge badgeContent={data.badgeContent}></Badge>
                                    )}
                                </Typography>

                                <Typography
                                    tabIndex={0}
                                    variant="boldBody2"
                                    alignItems="end"
                                    display="block"
                                    data-testid="increases-value"
                                    sx={{
                                        py: 2,
                                        color: 'common.fordTwilight.main',
                                        textAlign: 'right',
                                        textWrap: 'balance',
                                        flex: 1
                                    }}
                                >
                                    +{numberToCurrencyFormat(data.value)}
                                </Typography>
                            </Box>
                        ))}

                        <Divider
                            sx={{
                                height: '1px',
                                margin: { xs: '0 30px', md: '0 70px' },
                                backgroundColor: 'common.gray.8'
                            }}
                            orientation="vertical"
                            aria-hidden="true"
                        ></Divider>

                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                gap: { xs: 6, lg: 16 },
                                py: 4,
                                margin: { xs: '0 30px', md: '0 70px' }
                            }}
                        >
                            <Typography
                                tabIndex={0}
                                variant="boldBody2"
                                display="block"
                                sx={{
                                    color: 'common.gray.6',
                                    py: 2
                                }}
                                data-testid="increases-total-label"
                            >
                                {t('total_increases')}
                            </Typography>
                            <Typography
                                tabIndex={0}
                                variant="boldBody2"
                                alignItems="end"
                                display="block"
                                sx={{ py: 2, color: 'common.fordTwilight.main' }}
                                data-testid="increases-total-value"
                            >
                                +{numberToCurrencyFormat(formattedIncreaseTotal)}
                            </Typography>
                        </Box>
                    </>
                )}

                {Object.keys(increasesResponse)?.length > 0 && Object.keys(decreaseResponse)?.length > 0 && (
                    <Divider
                        sx={{ height: '8px', margin: '0 0 24px', backgroundColor: 'common.gray.7' }}
                        orientation="vertical"
                        aria-hidden="true"
                    ></Divider>
                )}

                {Object.keys(decreaseResponse)?.length > 0 && (
                    <>
                        <Box
                            sx={{
                                margin: { xs: '0 30px', md: '0 70px' }
                            }}
                        >
                            <Typography
                                variant="boldBody1"
                                sx={{
                                    py: 2,
                                    color: 'common.neutralBlack.dark'
                                }}
                                data-testid="decreases"
                                data-totaldec={Object.keys(decreaseResponse)?.length}
                            >
                                <Typography tabIndex={0} component="span" variant="boldBody1">
                                    {t('decreases')}
                                </Typography>
                                <PriceChangesInfo
                                    popOverData={decreasesPopOverData}
                                    transformOrigin={transformOrigin}
                                    isTotalPopOver={false}
                                />
                            </Typography>
                            <Divider
                                sx={{ height: '1px', backgroundColor: 'common.black.main' }}
                                orientation="vertical"
                                aria-hidden="true"
                            ></Divider>
                        </Box>

                        {priceDecrease?.details?.map((data) => (
                            <Box
                                key={data.name}
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    gap: { xs: 2, lg: 16 },
                                    py: 2,
                                    margin: { xs: '0 30px', md: '0 70px' }
                                }}
                            >
                                <Typography
                                    tabIndex={0}
                                    variant="regularBody2"
                                    display="block"
                                    sx={{
                                        color: 'common.gray.6',
                                        py: 2,
                                        textAlign: 'left',
                                        textWrap: 'balance',
                                        flex: { xs: 1.5, md: 2.1 }
                                    }}
                                    data-testid="decreases-label"
                                >
                                    {data?.name}
                                    {data?.badge && data.badgeContent && (
                                        <Badge badgeContent={data.badgeContent}></Badge>
                                    )}
                                </Typography>
                                <Typography
                                    tabIndex={0}
                                    variant="boldBody2"
                                    alignItems="end"
                                    display="block"
                                    data-testid="decreases-value"
                                    sx={{
                                        py: 2,
                                        color: 'common.fordTwilight.main',
                                        textAlign: 'right',
                                        textWrap: 'balance',
                                        flex: 1
                                    }}
                                >
                                    -{numberToCurrencyFormat(Number(String(data.value).replace('-', '')))}
                                </Typography>
                            </Box>
                        ))}

                        <Divider
                            sx={{
                                height: '1px',
                                margin: { xs: '0 30px', md: '0 70px' },
                                backgroundColor: 'common.gray.8'
                            }}
                            orientation="vertical"
                            aria-hidden="true"
                        ></Divider>

                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                gap: { xs: 6, lg: 16 },
                                py: 4,
                                margin: { xs: '0 30px', md: '0 70px' }
                            }}
                        >
                            <Typography
                                tabIndex={0}
                                variant="boldBody2"
                                display="block"
                                sx={{
                                    color: 'common.gray.6',
                                    py: 2
                                }}
                                data-testid="decreases-total-label"
                            >
                                {t('total_decreases')}
                            </Typography>
                            <Typography
                                tabIndex={0}
                                variant="boldBody2"
                                alignItems="end"
                                display="block"
                                sx={{ py: 2, color: 'common.fordTwilight.main' }}
                                data-testid="decreases-total-value"
                            >
                                -{numberToCurrencyFormat(formattedDecreaseTotal?.replace('-', ''))}
                            </Typography>
                        </Box>
                    </>
                )}
            </DialogContent>
        </MuiDialog>
    )
}

OldPriceComparisonDialog.propTypes = {
    isDialogOpen: PropTypes.bool,
    closeDialog: PropTypes.func,
    priceData: PropTypes.object
}

export default OldPriceComparisonDialog
